// src/containers/AuthenticationPage/GetStartedPage/GetStartedPage.js

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import { isScrollingDisabled } from '../../../ducks/ui.duck';
import { useConfiguration } from '../../../context/configurationContext';
import { handleExistingUserRedirect } from '../../../util/userRedirectHelper';
import { signup } from '../../../ducks/auth.duck';
import { isSignupEmailTakenError } from '../../../util/errors';
import { sendVerificationEmail } from '../../../ducks/user.duck';
import {
  Page,
  LayoutSingleColumn,
} from '../../../components';
import GetStartedForm from '../GetStartedForm/GetStartedForm';
import TermsAndConditions from '../TermsAndConditions/TermsAndConditions';
import TopbarContainer from '../../TopbarContainer/TopbarContainer';
import { handleReferralParams } from '../../../util/referralHelper';
import { ReferralTrackingHandler } from '../../../analytics/handlers/ReferralTrackingHandler';
import { supabase } from "../../../integrations/supabase/client";
import { callSignupWebhook } from '../../../util/secureWebhookCaller';

import css from './GetStartedPage.module.css';

import { Link } from 'react-router-dom';

// Updated to fix email verification flow - April 8, 2025

const GetStartedPageComponent = (props) => {
  const { submitSignup, history, intl, scrollingDisabled, currentUser, isAuthenticated, onResendVerificationEmail } = props;
  const config = useConfiguration();
  const { userFields, userTypes } = config.user || {};

  // Add useEffect to check for existing preferences and redirect if found
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    if (isAuthenticated && currentUser && !isRedirecting) {
      const redirectPath = handleExistingUserRedirect(currentUser);
      if (redirectPath && redirectPath !== '/getstarted') {
        console.log('User has preferences, redirecting to:', redirectPath);
        history.replace(redirectPath);
      }
    }
  }, [history, currentUser, isAuthenticated, isRedirecting]);

  // Add state for modal controls
  const [tosModalOpen, setTosModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);

  // Create an empty topbar component
  const topbar = <TopbarContainer className={css.topbar} hideMenu />;

  useEffect(() => {
    // Only run in browser environment
    if (typeof window !== 'undefined') {
      // Track page view in Facebook Pixel
      if (window.fbq) {
        window.fbq('track', 'PageView');
      }
      
      // Track page view with UTM parameters
      if (typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        if (window.fbq) {
          window.fbq('trackCustom', 'GetStartedPageView', {
            'utm_source': urlParams.get('utm_source') || '',
            'utm_medium': urlParams.get('utm_medium') || '',
            'utm_campaign': urlParams.get('utm_campaign') || '',
            'utm_content': urlParams.get('utm_content') || '',
            'utm_term': urlParams.get('utm_term') || '',
            'fb_click_id': urlParams.get('fbclid') || ''
          });
        }
      }
      
      // Track page view in Google Tag Manager if available
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'get_started_page_view',
          page_path: window.location.pathname,
          utm_source: urlParams.get('utm_source'),
          utm_medium: urlParams.get('utm_medium'),
          utm_campaign: urlParams.get('utm_campaign')
        });
      }
    }
  }, []);

  const handleSubmit = async (values) => {
    console.log('Form values:', values);
    const {
      pub_practice_types,
      pub_State,
      pub_License,
      pub_controlled_substances,
      pub_start_date,
      email,
      password,
      fname,
      lname,
      phone,
      termsAndConditions,
    } = values;

    // Add this line to get referral data
    const referralData = handleReferralParams();

    const params = {
      email,
      password,
      firstName: fname.trim(),
      lastName: lname.trim(),
      publicData: {
        userType: 'APP',
        pub_practice_types: Array.isArray(pub_practice_types) ? pub_practice_types : [pub_practice_types],
        pub_State: pub_State || '',
        pub_License: pub_License || '',
        pub_controlled_substances: pub_controlled_substances || '',
        pub_start_date: pub_start_date || '',
        phone: phone || '',
        // Add this line to include referral data in publicData
        ...(referralData && { referral: referralData })
      },
    };

    // Set redirecting flag to prevent email verification screen
    setIsRedirecting(true);
    
    try {
      await submitSignup(params);
      console.log('Signup and login successful');
      
      // Track conversion in Facebook Pixel
      if (typeof window !== 'undefined' && window.fbq) {
        try {
          window.fbq('track', 'CompleteRegistration', {
            content_name: 'get_started_form',
            status: 'complete',
            value: 1.00,
            currency: 'USD'
          });
          
          // Also track as Lead
          window.fbq('track', 'Lead', {
            content_name: 'GetStarted Completion',
            status: 'complete',
            value: 1.00,
            currency: 'USD'
          });
          
          console.log('Facebook conversion events fired successfully');
        } catch (pixelError) {
          console.error('Error firing Facebook Pixel events:', pixelError);
        }
      }
      
      // Track referral data first to ensure it's saved
      if (referralData) {
        const referralTracker = new ReferralTrackingHandler();
        referralTracker.trackReferral(referralData);
      }
      
      // Send verification email in the background without showing the screen
      await onResendVerificationEmail();
      
      // Call the Supabase webhook with user data AND WAIT for it to complete
      const userData = {
        userId: email, // Use email as temporary ID until we get the actual user ID
        email: email,
        firstName: fname.trim(),
        lastName: lname.trim(),
        displayName: `${fname.trim()} ${lname.trim()}`,
        publicData: {
          userType: 'APP',
          pub_practice_types: Array.isArray(pub_practice_types) ? pub_practice_types : [pub_practice_types],
          pub_State: pub_State || '',
          pub_License: pub_License || '',
          pub_controlled_substances: pub_controlled_substances || '',
          pub_start_date: pub_start_date || '',
          ...(referralData ? { referral: referralData } : {})
        },
        phone: phone || '',
        referralData: referralData,
        privateData: {},
        protectedData: {},
        metadata: {
          source: 'signup_form',
          timestamp: new Date().toISOString()
        },
        stripeAccountId: null,
        referralCode: null,
        referralsCount: 0,
        referralEarnings: 0
      };
      
      console.log('Calling webhook', { 
        hasUserData: !!userData,
        userType: userData?.publicData?.userType || 'unknown'
      });
      
      // Only call webhook during signup
      if (userData && userData.email) {
        const response = await callSignupWebhook(userData);
        
        console.log('Webhook response', { 
          success: response?.success,
          skipped: response?.skipped
        });
      }
      
      // Continue with redirect logic regardless of webhook result
      const searchParams = new URLSearchParams();
      if (params.publicData.pub_practice_types && params.publicData.pub_practice_types.length > 0) {
        searchParams.append('pub_practice_types', `has_all:${params.publicData.pub_practice_types.join(',')}`);
      }
      if (params.publicData.pub_State) {
        searchParams.append('pub_State', `has_all:${params.publicData.pub_State}`);
      }
      searchParams.append('sort', '-price');

      const searchQuery = searchParams.toString();

      // Check if there's a specific redirect path from existing user logic
      const redirectPath = handleExistingUserRedirect(userData);
      if (redirectPath && redirectPath !== '/getstarted') {
        console.log('Redirecting to:', redirectPath);
        history.push(redirectPath);
      } else {
        // Otherwise use our search parameters
        console.log(`Redirecting to: /s?${searchQuery}`);
        history.push(`/s?${searchQuery}`);
      }

      // Add Facebook Pixel tracking
      if (window.fbq) {
        // Track each step
        window.fbq('track', 'GetStartedStep', {
          step_number: 6,
          step_name: 'GetStarted Completion',
          completion_percentage: '100.00'
        });
        // If this is the final step, track it as a Lead conversion
        window.fbq('track', 'Lead', {
          content_name: 'GetStarted Completion',
          status: 'complete',
          value: 1.00,
          currency: 'USD'
        });
      }
    } catch (error) {
      secureLog.error('Error in signup flow', { message: error.message });
      // Continue with redirect anyway
      setIsRedirecting(false);
      
      console.error('Signup or login error:', error);
      if (isSignupEmailTakenError(error)) {
        // Handle email taken error
      }
    }
  };

  const schemaTitle = intl.formatMessage({ id: 'GetStartedPage.schemaTitle' });
  const schemaDescription = intl.formatMessage({ id: 'GetStartedPage.schemaDescription' });

  const termsAndConditions = (
    <TermsAndConditions 
      intl={intl}
      onOpenTermsOfService={() => setTosModalOpen(true)}
      onOpenPrivacyPolicy={() => setPrivacyModalOpen(true)}
    />
  );

  return (
    <Page
      className={css.root}
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: schemaTitle,
        description: schemaDescription,
      }}
    >
      <LayoutSingleColumn topbar={topbar}>
        <div className={css.contentContainer}>
          <div className={css.content}>
            <h1 className={css.title}>
              <FormattedMessage id="GetStartedPage.title" />
            </h1>
            <GetStartedForm
              onSubmit={handleSubmit}
              termsAndConditions={termsAndConditions}
              userFields={userFields || []}
              userTypes={userTypes || []}
            />
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

GetStartedPageComponent.defaultProps = {
  currentUser: null,
};

GetStartedPageComponent.propTypes = {
  currentUser: PropTypes.object,
  isAuthenticated: PropTypes.bool,
  intl: intlShape.isRequired,
  scrollingDisabled: PropTypes.bool.isRequired,
  submitSignup: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  onResendVerificationEmail: PropTypes.func.isRequired,
};

const mapDispatchToProps = (dispatch) => ({
  submitSignup: (params) => dispatch(signup(params)),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
});

const mapStateToProps = (state) => ({
  scrollingDisabled: isScrollingDisabled(state),
  currentUser: state.user.currentUser,
  isAuthenticated: state.auth.isAuthenticated,
});

const GetStartedPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(GetStartedPageComponent);

export default GetStartedPage;
